import React, { useEffect, useState } from 'react';

import Select from 'ui/Select/Select';
import { useAppDispatch } from 'ducks/hooks';
import {
  autofillDoorType,
  cornerWallLeftWidth,
  cornerWallRightWidth,
  setAutofillActive,
  setCornerWallLeftWidth,
  setCornerWallRightWidth
} from 'rangeConfig/pax/features/AutofillPax/AutofillPax';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import Title from 'ui/Typography/Title';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import { useBehaviorSystem } from 'contexts/behaviors';
import { getSelectedGroupId } from 'ducks/products';
import { getMajorityFrameDepth, setDisabledOptions } from '../AutofillUtils';
import { useCore } from 'contexts/core';
import { useTranslations } from 'hooks/useTranslations';
import { useServiceSettings } from 'contexts/serviceSettings';

const AutoFillLOptimizeSpaceCornerWallOptions = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const frameDepth = getMajorityFrameDepth(coreApi);
  const leftWallWidthValue = useSelector(cornerWallLeftWidth);
  const rightWallWidthValue = useSelector(cornerWallRightWidth);
  const selectedDoorType = useSelector(autofillDoorType);
  const { translations } = useServiceSettings();
  const behaviorSystem = useBehaviorSystem();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const { unit, useMetric } = useTranslations();

  type WidthOptions = {
    key: string;
    value: number;
    disabled: boolean;
  }[];

  const widthOptionsOrig: WidthOptions = [
    { key: '85', value: 860, disabled: false },
    { key: '110', value: 1110, disabled: false },
    { key: '135', value: 1360, disabled: false },
    { key: '160', value: 1610, disabled: false },
    { key: '185', value: 1860, disabled: false },
    { key: '210', value: 2110, disabled: false },
    { key: '235', value: 2360, disabled: false },
    { key: '260', value: 2610, disabled: false },
    { key: '285', value: 2860, disabled: false },
    { key: '310', value: 3110, disabled: false },
    { key: '335', value: 3360, disabled: false },
    { key: '360', value: 3610, disabled: false },
    { key: '385', value: 3860, disabled: false },
    { key: '410', value: 4110, disabled: false }
  ].map(w => ({
    ...w,
    key: `${adjustMeasurementsSystem(w.value, translations, useMetric)} ${unit}`
  }));

  const widthOptionsPlusOrig: WidthOptions = [
    { key: '88', value: 880, disabled: false },
    { key: '113', value: 1130, disabled: false },
    { key: '138', value: 1380, disabled: false },
    { key: '163', value: 1630, disabled: false },
    { key: '188', value: 1880, disabled: false },
    { key: '213', value: 2130, disabled: false },
    { key: '238', value: 2380, disabled: false },
    { key: '263', value: 2630, disabled: false },
    { key: '288', value: 2880, disabled: false },
    { key: '313', value: 3130, disabled: false },
    { key: '338', value: 3380, disabled: false },
    { key: '363', value: 3630, disabled: false },
    { key: '388', value: 3880, disabled: false },
    { key: '413', value: 4130, disabled: false }
  ].map(w => ({
    ...w,
    key: `${adjustMeasurementsSystem(w.value, translations, useMetric)} ${unit}`
  }));

  const [widthOptionsLeft, setWidthOptionsLeft] = useState<WidthOptions>(
    frameDepth > 350 ? [...widthOptionsOrig] : [...widthOptionsPlusOrig]
  );
  const [widthOptionsRight, setWidthOptionsRight] = useState<WidthOptions>(
    frameDepth > 350 ? [...widthOptionsOrig] : [...widthOptionsPlusOrig]
  );

  useEffect(() => {
    dispatch(setCornerWallLeftWidth(frameDepth > 350 ? 1110 : 1130));
    dispatch(setCornerWallRightWidth(frameDepth > 350 ? 1110 : 1130));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newWidthOptionsRight = [...widthOptionsRight].map(w => ({
      ...w,
      disabled: setDisabledOptions(
        w.value,
        leftWallWidthValue,
        frameDepth,
        frameDepth,
        selectedDoorType
      )
    }));

    const newWidthOptionsLeft = [...widthOptionsLeft].map(w => ({
      ...w,
      disabled: setDisabledOptions(
        w.value,
        rightWallWidthValue,
        frameDepth,
        frameDepth,
        selectedDoorType
      )
    }));

    setWidthOptionsRight(newWidthOptionsRight);
    setWidthOptionsLeft(newWidthOptionsLeft);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoorType]);

  const handleLeftWallWidthChange = (width: string) => {
    behaviorSystem.startReport(`autofill-corner-width-${width}`, {
      groupId: selectedGroupId,
      menuType: 'sheet',
      widgetId: 'autofill-corner-wall-left-width',
      meta: { size: { dimension: 'width', value: width } }
    });

    dispatch(setAutofillActive(true));
    dispatch(setCornerWallLeftWidth(Number(width)));

    const newWidthOptionsRight = [...widthOptionsRight].map(w => ({
      ...w,
      disabled: setDisabledOptions(
        w.value,
        Number.parseInt(width),
        frameDepth,
        frameDepth,
        selectedDoorType
      )
    }));
    setWidthOptionsRight(newWidthOptionsRight);
  };

  const handleRightWallWidthChange = (width: string) => {
    behaviorSystem.startReport(`autofill-corner-width-${width}`, {
      groupId: selectedGroupId,
      menuType: 'sheet',
      widgetId: 'autofill-corner-wall-right-width',
      meta: { size: { dimension: 'width', value: width } }
    });

    dispatch(setAutofillActive(true));
    dispatch(setCornerWallRightWidth(Number(width)));

    const newWidthOptionsLeft = [...widthOptionsLeft].map(w => ({
      ...w,
      disabled: setDisabledOptions(
        w.value,
        Number.parseInt(width),
        frameDepth,
        frameDepth,
        selectedDoorType
      )
    }));

    setWidthOptionsLeft(newWidthOptionsLeft);
  };

  return (
    <>
      <Title
        mb="4px"
        id="autofill-change-door-type-heading"
        type={ThemeFontStyleTypeEnum.headingXSmall}
      >
        {formatMessage({ id: 'storageone.autofillPaxCornerLeftSectionTitle' })}
      </Title>
      <Select
        testDataPrefix="left"
        data-testid="autoFillOptimizeSpaceCornerWallOptions-leftOptions-width"
        id="Autofill_corner_select_left_width"
        items={widthOptionsLeft}
        onChange={handleLeftWallWidthChange}
        value={leftWallWidthValue}
        hintText={formatMessage({ id: 'storageone.autofillPaxChooseAnOptionHint' })}
      />
      <Title
        mt="xl"
        mb="4px"
        id="autofill-change-door-type-heading"
        type={ThemeFontStyleTypeEnum.headingXSmall}
      >
        {formatMessage({ id: 'storageone.autofillPaxCornerRightSectionTitle' })}
      </Title>
      <Select
        testDataPrefix="right"
        data-testid="autoFillOptimizeSpaceCornerWallOptions-rightOptions-width"
        id="Autofill_corner_select_right_width"
        items={widthOptionsRight}
        onChange={handleRightWallWidthChange}
        value={rightWallWidthValue}
        hintText={formatMessage({ id: 'storageone.autofillPaxChooseAnOptionHint' })}
      />
    </>
  );
};

export default AutoFillLOptimizeSpaceCornerWallOptions;
